import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import throttle from 'lodash.throttle';
import sanitizeHtml from 'sanitize-html';
import { Button, Slider, Input, Row, Col, Form } from 'antd';
import { EditorElementPopover } from '../EditorElementPopover';
import {
  SwapOutlined,
} from '@ant-design/icons';

const FormItem = Form.Item;

export class PImage extends React.Component {
  constructor(props) {
    super(props);
    this.popoverRef = React.createRef();
  }

  state = {
    showBtn: false,

    altValidateStatus: 'success',
    altHelp: '',
  };

  static propTypes = {
    mediaUrlsafe: PropTypes.string, // urlsafe of the mediaobject

    alt: PropTypes.string, // alt text

    renderSize: PropTypes.string, // sm 640 | md 1280 | lg 1920

    brightness: PropTypes.number,
    contrast: PropTypes.number,
    saturation: PropTypes.number,
    hue: PropTypes.number,
    grayscale: PropTypes.number,

    className: PropTypes.string,
    style: PropTypes.object,

    address: PropTypes.array,
    updateNode: PropTypes.func,
    invokeGallery: PropTypes.func,
    srcForMedia: PropTypes.func,

    publish: PropTypes.bool,
  };

  static defaultProps = {
    mediaUrlsafe: null,

    // src: '',
    alt: '',
    renderSize: 'md',

    brightness: 100,
    contrast: 100,
    saturation: 100,
    hue: 180,
    grayscale: 0,
    publish: false,
  };

  replaceImage = () => {
    this.popoverRef.current.dismiss();
    this.props.invokeGallery(this.props.address, 'mediaUrlsafe', this.props.renderSize);
  };

  resetFilters = (e) => {
    this.props.updateNode(this.props.address, { brightness: 100 });
    this.props.updateNode(this.props.address, { contrast: 100 });
    this.props.updateNode(this.props.address, { saturation: 100 });
    this.props.updateNode(this.props.address, { hue: 180 });
    this.props.updateNode(this.props.address, { grayscale: 0 });
    e.preventDefault();
  };

  onAltChange = throttle((value) => {
    const sanitized = sanitizeHtml(value.trim(), {
      allowedTags: [],
      allowedAttributes: {},
    });
    if (sanitized.length === 0) {
      this.setState({
        altValidateStatus: 'warning',
        altHelp: 'Should not be empty',
      });
      this.props.updateNode(this.props.address, { alt: '' });
    }
    else if (sanitized.length > 64) {
      this.setState({
        altValidateStatus: 'warning',
        altHelp: 'Too long',
      });
    }
    else {
      this.setState({
        altValidateStatus: 'success',
        altHelp: null,
      });
      this.props.updateNode(this.props.address, { alt: sanitized });
    }
  }, 500);

  popoverContent = () => {
    const { brightness, contrast, saturation, hue, grayscale } = this.props;
    const filterOn =
    brightness !== 100 ||
    contrast !== 100 ||
    saturation !== 100 ||
    hue !== 180 ||
    grayscale !== 0;

    return (
      <React.Fragment>

        <Button type="primary" block icon={<SwapOutlined />} onClick={this.replaceImage}>
          Pick another image
        </Button>

        <div className="divider" />

        <div className="label">
          <div className="main">Alternate text</div>
          <div className="help">Fallback texts help screen-reader softwares, visually-impaired users, and search engines.</div>
        </div>

        <FormItem
          validateStatus={this.state.altValidateStatus}
          hasFeedback={false}
          help={this.state.altHelp}
        >
          <Input
            placeholder="Caption your image"
            onChange={(e) => { this.onAltChange(e.target.value); }}
          />
        </FormItem>

        <div className="divider" />

        <div className="label">
          <div className="main">Filters</div>
          <div className="help">
            Color-correct to match your theme. May not work on old browsers - roughly 1 in 40 cases.
          </div>
        </div>

        <Row>
          <Col span={8}><div className="filter-caption">brightness</div></Col>
          <Col span={16}>
            <Slider
              value={brightness}
              min={80} max={120} marks={{ 100: '' }}
              onChange={this.onBrightnessChange}
              tipFormatter={(v) => (v - 100)}
            />
          </Col>
        </Row>
        <Row>
          <Col span={8}><div className="filter-caption">contrast</div></Col>
          <Col span={16}>
            <Slider
              value={contrast}
              min={80} max={120} marks={{ 100: '' }}
              onChange={this.onContrastChange}
              tipFormatter={(v) => (v - 100)}
            />
          </Col>
        </Row>
        <Row>
          <Col span={8}><div className="filter-caption">saturation</div></Col>
          <Col span={16}>
            <Slider
              value={saturation}
              min={80} max={120} marks={{ 100: '' }}
              onChange={this.onSaturationChange}
              tipFormatter={(v) => (v - 100)}
            />
          </Col>
        </Row>
        <Row>
          <Col span={8}><div className="filter-caption">hue</div></Col>
          <Col span={16}>
            <Slider
              value={hue}
              min={0} max={360} marks={{ 180: '' }} step={2}
              onChange={this.onHueChange}
              tipFormatter={(v) => `${(v - 180)}deg`}
            />
          </Col>
        </Row>
        <Row>
          <Col span={8}><div className="filter-caption">grayscale</div></Col>
          <Col span={16}>
            <Slider
              value={grayscale}
              min={0} max={100} marks={{ 0: '' }}
              onChange={this.onGrayscaleChange}
            />
          </Col>
        </Row>

        {
          filterOn ? (
            <div className="text-center">
              <Button type="link" size="small" onClick={this.resetFilters}>Reset filters</Button>
            </div>
          ) : null
        }

      </React.Fragment>
    );
  };

  onBrightnessChange = throttle((value) => {
    this.props.updateNode(this.props.address, { brightness: value });
  }, 50);
  onContrastChange = throttle((value) => {
    this.props.updateNode(this.props.address, { contrast: value });
  }, 50);
  onSaturationChange = throttle((value) => {
    this.props.updateNode(this.props.address, { saturation: value });
  }, 50);
  onHueChange = throttle((value) => {
    this.props.updateNode(this.props.address, { hue: value });
  }, 50);
  onGrayscaleChange = throttle((value) => {
    this.props.updateNode(this.props.address, { grayscale: value });
  }, 50);

  onMouseOver = (e) => {
    this.setState({ showBtn: true });
    e.stopPropagation();
  };

  onMouseOut = (e) => {
    this.setState({ showBtn: false });
    e.stopPropagation();
  };

  getFilterStyle = () => {
    const { brightness, contrast, saturation, hue, grayscale } = this.props;
    const filterValue =
    `brightness(${brightness}%) ` +
    `contrast(${contrast}%) ` +
    `saturate(${saturation}%) ` +
    `hue-rotate(${hue - 180}deg) ` +
    `grayscale(${grayscale}%)`;
    return { filter: filterValue };

    // const bVal = `brightness(${brightness}%)`;
    // const cVal = `contrast(${contrast}%)`;
    // const sVal = `saturate(${saturation}%)`;
    // const hVal = `hue-rotate(${hue - 180}deg)`;
    // const gVal = `grayscale(${grayscale}%)`;
    //
    // return {
    //   filter: `${bVal} ${cVal} ${sVal} ${hVal} ${gVal}`,
    // }
  };

  render() {
    const { alt, renderSize, publish, mediaUrlsafe } = this.props;
    const filterStyle = this.getFilterStyle();
    const src = this.props.srcForMedia(mediaUrlsafe, renderSize);

    return (
      <div
        className={cx(
          'pimage',
          this.props.className,
        )}
        onMouseOver={this.onMouseOver}
        onFocus={this.onMouseOver}
        onMouseOut={this.onMouseOut}
        onBlur={this.onMouseOut}
      >
        <img
          src={src}
          alt={alt}
          style={{
            ...this.props.style,
            ...filterStyle,
          }}
        />

        <EditorElementPopover
          ref={this.popoverRef}
          usecase="image"
          showBtn={this.state.showBtn}
          btnLabel="image"
          title="Image appearance"
          content={this.popoverContent()}
          placement="bottomLeft"
          publish={publish}
        />

      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/components/editor/elements/PImage.jsx